import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDays, fmtMoney, fmtDate } from "../lib/format";
import KPI from "../components/KPI";
import Hourglass from "../components/Hourglass";
import VitaMeaTicker from "../components/VitaMeaTicker";
import TourGuide, { useTourGuide } from "../components/TourGuide";
import { ArrowRight, FileText, Calculator, Stamp, Scales, Trash, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Dashboard() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingRecap, setSendingRecap] = useState(false);
  const { showTour, closeTour } = useTourGuide();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.get("/analyses");
      setAnalyses(r.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette analyse ?")) return;
    await api.delete(`/analyses/${id}`);
    toast.success("Analyse supprimée");
    load();
  };

  const sendRecap = async () => {
    setSendingRecap(true);
    try {
      const r = await api.post("/email/recap/send-me");
      const s = r.data?.status;
      if (s === "sent") toast.success("Récap personnel envoyé sur votre email");
      else if (s === "dry_run") toast.info("Mode dry-run · récap loggé côté serveur");
      else toast.error((r.data?.error || "").slice(0, 180) || "Échec envoi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec envoi");
    } finally {
      setSendingRecap(false);
    }
  };

  const last = analyses[0];
  const v1 = last?.resultat?.vitalist?.V1 || 0;
  const v2 = last?.resultat?.vitalist?.V2 || 0;
  const d2 = last?.resultat?.dette?.D2 || 0;
  const srv = last?.resultat?.vitalist?.V5?.srv_global || 0;
  const qual = last?.resultat?.synthese?.qualification;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      {showTour && <TourGuide onClose={closeTour} />}
      <div className="flex items-end justify-between mb-10 fade-up flex-wrap gap-4">
        <div>
          <div className="label-mono">§ Dossier vitaliste</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Tableau de bord</h1>
        </div>
        <div className="flex items-center gap-4">
          {analyses.length > 0 && (
            <button
              onClick={sendRecap}
              disabled={sendingRecap}
              data-testid="dashboard-send-recap"
              className="border border-black px-4 py-2 hover:bg-black hover:text-[var(--paper)] label-mono flex items-center gap-2 disabled:opacity-50"
              title="Recevoir votre journal vitaliste par email"
            >
              <PaperPlaneTilt size={14} /> {sendingRecap ? "Envoi..." : "M'envoyer un récap"}
            </button>
          )}
          <div className="hidden sm:block label-mono text-right">
            <div>Pièces archivées</div>
            <div className="text-3xl font-serif font-medium text-[var(--ink)]" data-testid="dashboard-count">{analyses.length}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="border border-black/15 p-12 text-center label-mono">Chargement du dossier...</div>
      ) : !last ? (
        <EmptyState />
      ) : (
        <>
          {/* VITA-MEA — Live counter */}
          <div className="mb-8 fade-up">
            <VitaMeaTicker v1Annuel={v1} d2Dette={d2} devise={last.devise || "UM"} />
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-black/15 border border-black/15 mb-8 fade-up fade-up-delay-1">
            <div className="bg-[var(--paper)] p-8 lg:row-span-2 flex flex-col items-center justify-center">
              <Hourglass percent={Math.min(100, (v1/365)*100)} value={fmtDays(v1)} label="JOURS DE VIE SACRIFIÉS" sub={`${v2.toFixed(2)}% de l'année · V1`} />
            </div>
            <KPI testid="kpi-v3" label="V3 · Jour de Libération" value={fmtDate(last.resultat.vitalist.V3)} sub="Date à laquelle vous cessez de travailler pour l'État" />
            <KPI testid="kpi-d2" label="D2 · Dette en jours" value={fmtDays(d2)} sub="Jours de vie hypothéqués par la dette par citoyen" accent={d2 > 90} />
            <KPI testid="kpi-srv" label="V5 · Score Retour Vitaliste" value={`${srv.toFixed(1)}/100`} sub="Qualité pondérée des services restitués" />
            <KPI testid="kpi-v4" label="V4 · Bilan de carrière (40 ans)" value={`${(last.resultat.vitalist.V4.annees_totales).toFixed(1)} ans`} sub={`${fmtDays(last.resultat.vitalist.V4.jours_totaux)} sur 40 ans de carrière`} />
          </div>

          {/* Qualification */}
          {qual && (
            <div className="border border-black mb-8 fade-up fade-up-delay-2" data-testid="qualification-card">
              <div className="grid grid-cols-1 md:grid-cols-3">
                <div className="p-8 border-r border-black/15" style={{ background: qual.couleur, color: "#fff" }}>
                  <div className="label-mono opacity-90">Qualification juridique</div>
                  <div className="mt-3 font-serif text-4xl font-medium">{qual.niveau}</div>
                  <div className="mt-2 font-mono text-xs opacity-90">Indicateur Jvita = {fmtDays(last.resultat.synthese.jvita, 1)}</div>
                </div>
                <div className="md:col-span-2 p-8">
                  <div className="label-mono">Qualification</div>
                  <p className="mt-2 font-serif text-2xl">{qual.qualification}</p>
                  <div className="mt-4 label-mono">Conséquences</div>
                  <p className="mt-2 text-[var(--muted)] leading-relaxed">{qual.consequence}</p>
                </div>
              </div>
            </div>
          )}

          {/* Quick links */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/15 border border-black/15 mb-10">
            {[
              { to: "/calculator", icon: Calculator, label: "Nouveau calcul" },
              { to: "/documents", icon: FileText, label: "Importer un PDF" },
              { to: "/reports", icon: Stamp, label: "Générer DVC/RRC" },
              { to: "/judgment", icon: Scales, label: "Simuler jugement" },
            ].map((it) => (
              <Link to={it.to} key={it.to} data-testid={`quick-${it.to.slice(1)}`}
                className="bg-[var(--paper)] p-6 flex items-center justify-between hover:bg-[var(--paper-2)] transition-colors group">
                <div className="flex items-center gap-3">
                  <it.icon size={22} weight="duotone" />
                  <span className="font-serif text-lg">{it.label}</span>
                </div>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform text-[var(--signal)]" />
              </Link>
            ))}
          </div>

          {/* History */}
          <div className="border border-black/15">
            <div className="flex items-center justify-between p-6 border-b border-black/15">
              <div>
                <div className="label-mono">§ Pièces archivées</div>
                <h2 className="font-serif text-2xl mt-1">Historique des analyses</h2>
              </div>
              <Link to="/calculator" className="border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)]">+ Nouvelle</Link>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-black/15 label-mono text-left">
                  <th className="p-4">N°</th>
                  <th className="p-4">Libellé</th>
                  <th className="p-4">Date</th>
                  <th className="p-4 text-right">V1 (jours)</th>
                  <th className="p-4 text-right">D2 (jours)</th>
                  <th className="p-4 text-right">SRV</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {analyses.map((a, i) => (
                  <tr key={a.id} className="border-b border-black/10 hover:bg-[var(--paper-2)]" data-testid={`row-analyse-${a.id}`}>
                    <td className="p-4 font-mono text-xs">{String(analyses.length - i).padStart(3, "0")}</td>
                    <td className="p-4 font-serif">{a.libelle}</td>
                    <td className="p-4 font-mono text-xs">{fmtDate(a.cree_le)}</td>
                    <td className="p-4 text-right tabular-nums">{fmtDays(a.resultat?.vitalist?.V1 || 0, 1)}</td>
                    <td className="p-4 text-right tabular-nums">{fmtDays(a.resultat?.dette?.D2 || 0, 1)}</td>
                    <td className="p-4 text-right tabular-nums">{(a.resultat?.vitalist?.V5?.srv_global || 0).toFixed(1)}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => remove(a.id)} className="text-[var(--muted)] hover:text-[var(--signal)]" data-testid={`del-analyse-${a.id}`}>
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-black p-16 text-center bg-[var(--paper-2)]" data-testid="dashboard-empty">
      <div className="label-mono">§ Dossier vide</div>
      <h2 className="mt-4 font-serif text-4xl font-medium">Aucune pièce archivée</h2>
      <p className="mt-3 text-[var(--muted)] max-w-md mx-auto">
        Lancez votre première analyse vitaliste pour calculer votre pression fiscale en jours de vie.
      </p>
      <Link to="/calculator" data-testid="empty-cta-calc"
        className="inline-flex mt-8 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-3 label-mono items-center gap-2">
        Ouvrir VITA-ENGINE <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}
